import {LABELS} from '../../../shared/labels/Labels';
import {XeroSignupAction} from '../../actions';
import {Tenant} from '../../types/Types';
import {
  StyledBannerWrapper,
  StyledButtonWrapper,
  StyledLinkWrapper,
  StyledWrapper,
} from './styled';
import {useGlobalization} from '@planday-corp/globalization';
import Button from '@planday-corp/ui/actions/Button';
import Link from '@planday-corp/ui/actions/Link';
import SelectField from '@planday-corp/ui/forms/fields/SelectField';
import Banner from '@planday-corp/ui/indicators/Banner';
import EditModal from '@planday-corp/ui/overlays/modals/EditModal';
import I18n from '@planday-corp/ui/text/I18n';
import Text from '@planday-corp/ui/text/Text';
import React, {useEffect, useMemo, useState} from 'react';

export interface OrganisationSelectorProps {
  tenants: Tenant[] | undefined;
  tenantId: string | undefined;
  signupUrl: string | undefined;
  setTenantIdAction: (tenantId: string) => XeroSignupAction;
  proceedAction: (tenantId: string | undefined) => XeroSignupAction;
  fetchFormDataAction: () => XeroSignupAction;
}

export default function OrganisationSelector({
  tenants,
  tenantId,
  signupUrl,
  setTenantIdAction,
  proceedAction,
  fetchFormDataAction,
}: OrganisationSelectorProps) {
  const {i18n} = useGlobalization();

  const [showModal, setShowModal] = useState(false);

  const showOptions = useMemo(() => {
    return tenants!.length === 1 ? false : true;
  }, [tenants]);

  useEffect(() => {
    if (!showOptions && tenants) {
      setTenantIdAction(tenants[0].id);
    }
  }, [showOptions]);

  const tenant = useMemo(() => {
    if (tenantId !== undefined) {
      return tenants!.find(item => item.id === tenantId);
    }
  }, [tenantId]);

  const options = useMemo(() => {
    return tenants!.map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [tenants]);

  const handleClick = () => {
    setShowModal(true);
  };

  const modalDetails =
    tenant &&
    (tenant!.portalUrl
      ? {
          title: i18n.t(LABELS.loginModalTitle),
          primaryBtn: i18n.t(LABELS.loginBtn),
          body: i18n.t({
            term: 'SignUp.AuthorizationPage_LoginModal_Description',
            fallback:
              'Are you sure you want to log in to your Planday portal for the Xero organisation {0}?',
            params: [tenant.name],
          }),
        }
      : {
          title: i18n.t(LABELS.signupModalTitle),
          primaryBtn: i18n.t(LABELS.signupBtn),
          body: i18n.t({
            term: 'SignUp.AuthorizationPage_SignupModal_Description',
            fallback:
              'Are you sure you want to start a free Planday trial for your Xero organisation {0}?',
            params: [tenant.name],
          }),
        });

  return (
    <>
      {tenant?.portalUrl && (
        <StyledBannerWrapper>
          <Banner type="warning" padding="large" iconName="info">
            <Text
              element="span"
              value={{
                term: 'SignUp.AuthorizationPage_InfoBannerText',
                fallback:
                  'We found an existing Planday portal for the Xero organsation {0}. Click continue to log in.',
                params: [tenant.name],
              }}
            />
          </Banner>
        </StyledBannerWrapper>
      )}

      <Text size="label" element="label">
        {LABELS.xeroOrganisations}
      </Text>
      {options &&
        (showOptions ? (
          <StyledWrapper>
            <SelectField
              options={options}
              name="selectTenants"
              value={tenantId}
              onChange={val => setTenantIdAction(val as string)}
            />
          </StyledWrapper>
        ) : (
          <StyledWrapper>
            <Text size="body" element="span" value={options[0].label}></Text>
          </StyledWrapper>
        ))}

      {showModal && (
        <EditModal
          onClose={() => setShowModal(false)}
          ariaLabel="confirmModal"
          title={modalDetails!.title}
          backgroundColor="white"
          isBlocked
          buttons={[
            <Button
              key="close"
              type="basic"
              onClick={() => setShowModal(false)}
            >
              <I18n
                txtOrDescriptor={{
                  term: 'CommonTerms.Cancel',
                  fallback: 'Cancel',
                }}
              />
            </Button>,
            <Button
              key="proceed"
              type="primary"
              onClick={() =>
                tenant!.portalUrl
                  ? proceedAction(tenantId)
                  : fetchFormDataAction()
              }
            >
              {modalDetails!.primaryBtn}
            </Button>,
          ]}
        >
          <Text element="p" size="body" value={modalDetails!.body} />
        </EditModal>
      )}

      <StyledButtonWrapper>
        <StyledLinkWrapper>
          <Link href={signupUrl}>{i18n.t(LABELS.linkChooseAnotherOrg)}</Link>
        </StyledLinkWrapper>
        <Button
          key="continue"
          type="primary"
          onClick={handleClick}
          isDisabled={!tenantId}
        >
          {i18n.t(LABELS.continue)}
        </Button>
      </StyledButtonWrapper>
    </>
  );
}
